import Layout from '@/components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, LineChart, Line } from 'recharts';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { HiOutlineSparkles } from "react-icons/hi";
import { motion } from "framer-motion";

const data = [
    { name: 'Jan', uv: 4000, pv: 2400, amt: 2400 },
    { name: 'Feb', uv: 3000, pv: 1398, amt: 2210 },
    { name: 'Mar', uv: 2000, pv: 9800, amt: 2290 },
    { name: 'Apr', uv: 2780, pv: 3908, amt: 2000 },
    { name: 'May', uv: 1890, pv: 4800, amt: 2181 },
    { name: 'Jun', uv: 2390, pv: 3800, amt: 2500 },
];

const tableData = [
    { id: 1, name: 'Website A', status: 'Online', uptime: '99.9%', responseTime: '120ms' },
    { id: 2, name: 'API Service', status: 'Online', uptime: '99.7%', responseTime: '200ms' },
    { id: 3, name: 'Database Cluster', status: 'Degraded', uptime: '98.5%', responseTime: '350ms' },
    { id: 4, name: 'CDN', status: 'Online', uptime: '99.95%', responseTime: '50ms' },
];

const statusUpdate ="All systems are running smoothly. No active incidents. Orders are being placed as expected."

const fadeInVariants = {
  hidden: { opacity: 0, scale: 0.95 },
  visible: { opacity: 1, scale: 1 }
};

const Overview = () => {
    return (
        <Layout>
            <motion.div
                variants={fadeInVariants}
                initial="hidden"
                animate="visible"
                transition={{ duration: 0.5 }}
                className="mb-8 bg-gradient-to-r from-violet-600 to-violet-400 rounded-lg shadow-lg overflow-hidden"
            >
                <div className="px-4 py-6 md:px-8 md:py-8 flex items-center justify-between">
                    <div className="flex-1">
                        <h2 className="text-xl sm:text-2xl font-bold text-white mb-2 flex items-center">
                            Bot Insight™
                            <span className="ml-2 text-xs bg-white/20 text-white px-2 py-1 rounded-full flex items-center">
                                <HiOutlineSparkles className="w-4 h-4 mr-1" />
                                AI-Generated
                            </span>
                        </h2>
                        <p className="text-base sm:text-lg text-white/90">{statusUpdate}</p>
                    </div>
                </div>
            </motion.div>

            <motion.div
                variants={fadeInVariants}
                initial="hidden"
                animate="visible"
                transition={{ duration: 0.5, delay: 0.2 }}
                className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6 mb-6 md:mb-8"
            >
                <Card className="p-4 md:p-6">
                    <CardHeader className="p-0 md:p-2">
                        <CardTitle className="text-lg md:text-xl">Total Monitors</CardTitle>
                    </CardHeader>
                    <CardContent className="p-0 md:p-2 pt-2 md:pt-4">
                        <p className="text-2xl md:text-3xl font-bold">12</p>
                    </CardContent>
                </Card>
                <Card className="p-4 md:p-6">
                    <CardHeader className="p-0 md:p-2">
                        <CardTitle className="text-lg md:text-xl">Overall Uptime</CardTitle>
                    </CardHeader>
                    <CardContent className="p-0 md:p-2 pt-2 md:pt-4">
                        <p className="text-2xl md:text-3xl font-bold">99.8%</p>
                    </CardContent>
                </Card>
                <Card className="p-4 md:p-6">
                    <CardHeader className="p-0 md:p-2">
                        <CardTitle className="text-lg md:text-xl">Active Incidents</CardTitle>
                    </CardHeader>
                    <CardContent className="p-0 md:p-2 pt-2 md:pt-4">
                        <p className="text-2xl md:text-3xl font-bold">1</p>
                    </CardContent>
                </Card>
            </motion.div>

            <motion.div
                variants={fadeInVariants}
                initial="hidden"
                animate="visible"
                transition={{ duration: 0.5, delay: 0.4 }}
                className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8"
            >
                <Card>
                    <CardHeader>
                        <CardTitle>Response Time (ms)</CardTitle>
                    </CardHeader>
                    <CardContent className="h-[200px] sm:h-[250px] md:h-[300px]">
                        <ResponsiveContainer width="100%" height="100%">
                            <LineChart data={data}>
                                <CartesianGrid strokeDasharray="3 3" />
                                <XAxis dataKey="name" />
                                <YAxis />
                                <Tooltip />
                                <Legend />
                                <Line type="monotone" dataKey="pv" stroke="#a78bfa" activeDot={{ r: 8 }} />
                                <Line type="monotone" dataKey="uv" stroke="#6ee7b7" />
                            </LineChart>
                        </ResponsiveContainer>
                    </CardContent>
                </Card>
                <Card>
                    <CardHeader>
                        <CardTitle>Requests per Minute</CardTitle>
                    </CardHeader>
                    <CardContent className="h-[200px] sm:h-[250px] md:h-[300px]">
                        <ResponsiveContainer width="100%" height="100%">
                            <BarChart data={data}>
                                <CartesianGrid strokeDasharray="3 3" />
                                <XAxis dataKey="name" />
                                <YAxis />
                                <Tooltip />
                                <Legend />
                                <Bar dataKey="pv" fill="#a78bfa" />
                                <Bar dataKey="uv" fill="#6ee7b7" />
                            </BarChart>
                        </ResponsiveContainer>
                    </CardContent>
                </Card>
            </motion.div>

            <motion.div
                variants={fadeInVariants}
                initial="hidden"
                animate="visible"
                transition={{ duration: 0.5, delay: 0.6 }}
            >
            <Card>
                <CardHeader>
                    <CardTitle>Active Monitors</CardTitle>
                </CardHeader>
                <CardContent>
                    <Table>
                        <TableHeader>
                            <TableRow>
                                <TableHead>Name</TableHead>
                                <TableHead>Status</TableHead>
                                <TableHead>Uptime</TableHead>
                                <TableHead>Response Time</TableHead>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {tableData.map((row) => (
                                <TableRow key={row.id}>
                                    <TableCell>{row.name}</TableCell>
                                    <TableCell>
                                        <span className={`inline-block px-2 py-1 rounded-full text-xs font-semibold ${row.status === 'Online' ? 'bg-green-100 text-green-800' :
                                            row.status === 'Degraded' ? 'bg-yellow-100 text-yellow-800' :
                                                'bg-red-100 text-red-800'
                                            }`}>
                                            {row.status}
                                        </span>
                                    </TableCell>
                                    <TableCell>{row.uptime}</TableCell>
                                    <TableCell>{row.responseTime}</TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </CardContent>
            </Card>
            </motion.div>
        </Layout>
    );
};

export default Overview;
